import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown, Check } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { TextInput } from './ui';
import {
  rangeError,
  gpaError,
  hasNoErrors,
  RANGE_SAT_SECTION,
  RANGE_SAT_TOTAL,
  RANGE_PSAT_SECTION,
  RANGE_PSAT_TOTAL,
  RANGE_ACT,
} from '../utils/validation';

/* Focus indicator: the score and GPA fields below set no `outline` at all, on
 * purpose. An inline `outline: none` outranks the global `:focus-visible` rule
 * in styles/globals.css whatever its specificity, and nothing here draws a
 * replacement, so suppressing it left a keyboard user with no focus indicator
 * on any of them. */

const academicErrorStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '6px',
  fontSize: '13px',
  color: '#DC2626',
};

// GPA Scale options
const GPA_SCALE_OPTIONS = [
  { label: 'Unweighted 4.0 Scale (0.0 - 4.0)', value: '4.0' },
  { label: 'Weighted 5.0 Scale (0.0 - 5.0)', value: '5.0' },
  { label: 'Weighted 6.0 Scale (0.0 - 6.0)', value: '6.0' },
];

interface AcademicInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  athleteId: string;
  onUpdate: () => void;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

interface AcademicFormData {
  satMath: string;
  satReadingWriting: string;
  satTotal: string;
  psatMath: string;
  psatReadingWriting: string;
  psatTotal: string;
  actTotal: string;
  gpaScale: string;
  currentGpa: string;
}

const AcademicInfoEditModal: React.FC<AcademicInfoEditModalProps> = ({
  isOpen,
  onClose,
  initialData,
  athleteId,
  onUpdate,
  embedded = false,
  onSaved,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();

  const [formData, setFormData] = useState<AcademicFormData>({
    satMath: '',
    satReadingWriting: '',
    satTotal: '',
    psatMath: '',
    psatReadingWriting: '',
    psatTotal: '',
    actTotal: '',
    gpaScale: '',
    currentGpa: '',
  });

  const [showGpaScalePicker, setShowGpaScalePicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        satMath: initialData.satMath || '',
        satReadingWriting: initialData.satReadingWriting || '',
        satTotal: initialData.satTotal || '',
        psatMath: initialData.psatMath || '',
        psatReadingWriting: initialData.psatReadingWriting || '',
        psatTotal: initialData.psatTotal || '',
        actTotal: initialData.actTotal || '',
        gpaScale: initialData.gpaScale || '',
        currentGpa: initialData.currentGpa || '',
      });
      setHasChanges(false);
    }
  }, [isOpen, initialData]);

  // Auto-calculate SAT total
  useEffect(() => {
    const satMath = parseInt(formData.satMath) || 0;
    const satReadingWriting = parseInt(formData.satReadingWriting) || 0;
    const total = satMath + satReadingWriting;
    
    if ((satMath > 0 || satReadingWriting > 0) || (satMath === 0 && satReadingWriting === 0)) {
      updateFormData('satTotal', total > 0 ? total.toString() : '');
    }
  }, [formData.satMath, formData.satReadingWriting]);

  // Auto-calculate PSAT total
  useEffect(() => {
    const psatMath = parseInt(formData.psatMath) || 0;
    const psatReadingWriting = parseInt(formData.psatReadingWriting) || 0;
    const total = psatMath + psatReadingWriting;
    
    if ((psatMath > 0 || psatReadingWriting > 0) || (psatMath === 0 && psatReadingWriting === 0)) {
      updateFormData('psatTotal', total > 0 ? total.toString() : '');
    }
  }, [formData.psatMath, formData.psatReadingWriting]);

  const updateFormData = (field: keyof AcademicFormData, value: any) => {
    // Auto-calculated totals are readonly derivations — don't count them as a
    // user edit or the modal would flag itself dirty right after loading.
    if (field !== 'satTotal' && field !== 'psatTotal') {
      setHasChanges(true);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // #189 — sanity ranges + GPA-scale check; blocks Save on garbage input.
  const fieldErrors = useMemo(() => ({
    satMath: rangeError(formData.satMath, RANGE_SAT_SECTION, { integer: true }),
    satReadingWriting: rangeError(formData.satReadingWriting, RANGE_SAT_SECTION, { integer: true }),
    satTotal: rangeError(formData.satTotal, RANGE_SAT_TOTAL, { integer: true }),
    psatMath: rangeError(formData.psatMath, RANGE_PSAT_SECTION, { integer: true }),
    psatReadingWriting: rangeError(formData.psatReadingWriting, RANGE_PSAT_SECTION, { integer: true }),
    psatTotal: rangeError(formData.psatTotal, RANGE_PSAT_TOTAL, { integer: true }),
    actTotal: rangeError(formData.actTotal, RANGE_ACT, { integer: true }),
    currentGpa: gpaError(formData.currentGpa, formData.gpaScale),
  }), [formData]);

  const hasErrors = !hasNoErrors(fieldErrors);

  const handleSave = async () => {
    if (!sessionToken || !athleteId) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    // #164 — if the underlying academic data never loaded, refuse to save so we
    // don't overwrite real SAT/ACT/GPA with blanks.
    if (!initialData) {
      toast.error('Academic info could not be loaded. Close and retry before saving.');
      return;
    }

    if (hasErrors) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }

    setIsSaving(true);
    try {
      await athleteAPI.updateAcademicPerformance(sessionToken, athleteId, formData);

      toast.success('Academic info saved.');
      onUpdate();
      (onSaved || onClose)();
    } catch (error: any) {
      console.error('Error saving academic information:', error);
      toast.error('Failed to save academic information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="academic-title"
      contentStyle={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: isMobile ? 'calc(100vw - 32px)' : '90%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
          }}>
            <h2 id="academic-title" style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0,
            }}>
              Edit Academic Information
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
          }}>
            {/* SAT Scores Section */}
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '18px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                }}>
                  <ChevronDown size={18} color="#F9550D" strokeWidth={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: '2px',
                  }}>
                    SAT Scores
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    margin: 0,
                  }}>
                    Enter your most recent SAT scores
                  </p>
                </div>
              </div>

              {/* SAT Scores Row */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block',
                  }} htmlFor="academic-sat-math">
                    Math
                  </label>
                  <TextInput
                    id="academic-sat-math"
                    invalid={!!fieldErrors.satMath}
                    aria-describedby={fieldErrors.satMath ? 'academic-sat-math-error' : undefined}
                    type="number"
                    value={formData.satMath}
                    onChange={(e) => updateFormData('satMath', e.target.value)}
                    placeholder="800"
                    min={RANGE_SAT_SECTION.min}
                    max={RANGE_SAT_SECTION.max}
                    style={{
                      width: '100%',
                      height: '44px',
                      border: `1px solid ${fieldErrors.satMath ? '#DC2626' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                    }}
                  />
                  {fieldErrors.satMath && <span id="academic-sat-math-error" role="alert" style={academicErrorStyle}>{fieldErrors.satMath}</span>}
                </div>

                <div>
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block',
                  }} htmlFor="academic-sat-reading-writing">
                    Reading & Writing
                  </label>
                  <TextInput
                    id="academic-sat-reading-writing"
                    invalid={!!fieldErrors.satReadingWriting}
                    aria-describedby={fieldErrors.satReadingWriting ? 'academic-sat-reading-writing-error' : undefined}
                    type="number"
                    value={formData.satReadingWriting}
                    onChange={(e) => updateFormData('satReadingWriting', e.target.value)}
                    placeholder="800"
                    min={RANGE_SAT_SECTION.min}
                    max={RANGE_SAT_SECTION.max}
                    style={{
                      width: '100%',
                      height: '44px',
                      border: `1px solid ${fieldErrors.satReadingWriting ? '#DC2626' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                    }}
                  />
                  {fieldErrors.satReadingWriting && <span id="academic-sat-reading-writing-error" role="alert" style={academicErrorStyle}>{fieldErrors.satReadingWriting}</span>}
                </div>
              </div>

              {/* SAT Total */}
              <div>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px',
                  display: 'block',
                }} htmlFor="academic-sat-total">
                  Total Score
                </label>
                <TextInput
                  id="academic-sat-total"
                  type="text"
                  value={formData.satTotal}
                  readOnly
                  placeholder="1600"
                  style={{
                    width: '100%',
                    height: '44px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    background: '#F3F4F6',
                    padding: '0 12px',
                    fontSize: '14px',
                    color: '#6B7280',
                    boxSizing: 'border-box',
                  }}
                />
                {fieldErrors.satTotal && <span id="academic-sat-total-error" role="alert" style={academicErrorStyle}>{fieldErrors.satTotal}</span>}
              </div>
            </div>

            {/* PSAT Scores Section */}
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '18px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                }}>
                  <ChevronDown size={18} color="#F9550D" strokeWidth={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: '2px',
                  }}>
                    PSAT Scores
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    margin: 0,
                  }}>
                    Enter your most recent PSAT scores
                  </p>
                </div>
              </div>

              {/* PSAT Scores Row */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block',
                  }} htmlFor="academic-psat-math">
                    Math
                  </label>
                  <TextInput
                    id="academic-psat-math"
                    invalid={!!fieldErrors.psatMath}
                    aria-describedby={fieldErrors.psatMath ? 'academic-psat-math-error' : undefined}
                    type="number"
                    value={formData.psatMath}
                    onChange={(e) => updateFormData('psatMath', e.target.value)}
                    placeholder="760"
                    min={RANGE_PSAT_SECTION.min}
                    max={RANGE_PSAT_SECTION.max}
                    style={{
                      width: '100%',
                      height: '44px',
                      border: `1px solid ${fieldErrors.psatMath ? '#DC2626' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                    }}
                  />
                  {fieldErrors.psatMath && <span id="academic-psat-math-error" role="alert" style={academicErrorStyle}>{fieldErrors.psatMath}</span>}
                </div>

                <div>
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block',
                  }} htmlFor="academic-psat-reading-writing">
                    Reading & Writing
                  </label>
                  <TextInput
                    id="academic-psat-reading-writing"
                    invalid={!!fieldErrors.psatReadingWriting}
                    aria-describedby={fieldErrors.psatReadingWriting ? 'academic-psat-reading-writing-error' : undefined}
                    type="number"
                    value={formData.psatReadingWriting}
                    onChange={(e) => updateFormData('psatReadingWriting', e.target.value)}
                    placeholder="760"
                    min={RANGE_PSAT_SECTION.min}
                    max={RANGE_PSAT_SECTION.max}
                    style={{
                      width: '100%',
                      height: '44px',
                      border: `1px solid ${fieldErrors.psatReadingWriting ? '#DC2626' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                    }}
                  />
                  {fieldErrors.psatReadingWriting && <span id="academic-psat-reading-writing-error" role="alert" style={academicErrorStyle}>{fieldErrors.psatReadingWriting}</span>}
                </div>
              </div>

              {/* PSAT Total */}
              <div>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px',
                  display: 'block',
                }} htmlFor="academic-psat-total">
                  Total Score
                </label>
                <TextInput
                  id="academic-psat-total"
                  type="text"
                  value={formData.psatTotal}
                  readOnly
                  placeholder="1520"
                  style={{
                    width: '100%',
                    height: '44px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    background: '#F3F4F6',
                    padding: '0 12px',
                    fontSize: '14px',
                    color: '#6B7280',
                    boxSizing: 'border-box',
                  }}
                />
                {fieldErrors.psatTotal && <span id="academic-psat-total-error" role="alert" style={academicErrorStyle}>{fieldErrors.psatTotal}</span>}
              </div>
            </div>

            {/* ACT Score Section */}
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '18px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                }}>
                  <ChevronDown size={18} color="#F9550D" strokeWidth={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: '2px',
                  }}>
                    ACT Score
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    margin: 0,
                  }}>
                    Enter your most recent ACT score
                  </p>
                </div>
              </div>

              <div>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px',
                  display: 'block',
                }} htmlFor="academic-act-total">
                  Total Score
                </label>
                <TextInput
                  id="academic-act-total"
                  invalid={!!fieldErrors.actTotal}
                  aria-describedby={fieldErrors.actTotal ? 'academic-act-total-error' : undefined}
                  type="number"
                  value={formData.actTotal}
                  onChange={(e) => updateFormData('actTotal', e.target.value)}
                  placeholder="36"
                  min={RANGE_ACT.min}
                  max={RANGE_ACT.max}
                  style={{
                    width: '100%',
                    height: '44px',
                    border: `1px solid ${fieldErrors.actTotal ? '#DC2626' : '#D1D5DB'}`,
                    borderRadius: '8px',
                    padding: '0 12px',
                    fontSize: '14px',
                    color: '#1F2937',
                    boxSizing: 'border-box',
                  }}
                />
                {fieldErrors.actTotal && <span id="academic-act-total-error" role="alert" style={academicErrorStyle}>{fieldErrors.actTotal}</span>}
              </div>
            </div>

            {/* GPA Section */}
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '18px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                }}>
                  <ChevronDown size={18} color="#F9550D" strokeWidth={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0,
                    marginBottom: '2px',
                  }}>
                    GPA
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#6B7280',
                    margin: 0,
                  }}>
                    Current grade point average
                  </p>
                </div>
              </div>

              {/* GPA Scale */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px',
                  display: 'block',
                }}>
                  GPA Scale
                </label>
                <div
                  onClick={() => setShowGpaScalePicker(true)}
                  style={{
                    width: '100%',
                    height: '44px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    background: 'white',
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                >
                  <span style={{ color: formData.gpaScale ? '#1F2937' : '#9CA3AF' }}>
                    {formData.gpaScale 
                      ? GPA_SCALE_OPTIONS.find(opt => opt.value === formData.gpaScale)?.label 
                      : 'Select GPA Scale'}
                  </span>
                  <ChevronDown size={18} color="#6B7280" />
                </div>
              </div>

              {/* Current GPA */}
              {formData.gpaScale && (
                <div>
                  <label style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block',
                  }} htmlFor="academic-current-gpa">
                    Current GPA
                  </label>
                  <TextInput
                    id="academic-current-gpa"
                    invalid={!!fieldErrors.currentGpa}
                    aria-describedby={fieldErrors.currentGpa ? 'academic-current-gpa-error' : undefined}
                    type="text"
                    value={formData.currentGpa}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and one decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        const numValue = parseFloat(value);
                        const maxGpa = parseFloat(formData.gpaScale);
                        if (value === '' || (numValue >= 0 && numValue <= maxGpa)) {
                          updateFormData('currentGpa', value);
                        }
                      }
                    }}
                    placeholder={`Enter GPA (0.0 - ${formData.gpaScale})`}
                    maxLength={4}
                    style={{
                      width: '100%',
                      height: '44px',
                      border: `1px solid ${fieldErrors.currentGpa ? '#DC2626' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                      color: '#1F2937',
                      boxSizing: 'border-box',
                    }}
                  />
                  {fieldErrors.currentGpa && <span id="academic-current-gpa-error" role="alert" style={academicErrorStyle}>{fieldErrors.currentGpa}</span>}
                </div>
              )}
            </div>

          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
          }}>
            <button
              onClick={onClose}
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                background: 'white',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || hasErrors}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                background: (isSaving || hasErrors) ? '#9CA3AF' : '#F9550D',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (isSaving || hasErrors) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
    </Modal>

      {/* GPA Scale Picker Modal */}
      {showGpaScalePicker && (
        <div
          onClick={() => setShowGpaScalePicker(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10010,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90%',
              maxWidth: '500px',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
            }}>
              <button
                onClick={() => setShowGpaScalePicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  color: '#6B7280',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                margin: 0,
              }}>
                Select GPA Scale
              </h3>
              <button
                onClick={() => setShowGpaScalePicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#F9550D',
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
            }}>
              {GPA_SCALE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    updateFormData('gpaScale', option.value);
                    setShowGpaScalePicker(false);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 24px',
                    borderBottom: '1px solid #F3F4F6',
                    background: formData.gpaScale === option.value ? 'rgba(249, 85, 13, 0.1)' : 'white',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{
                    fontSize: '14px',
                    color: '#1F2937',
                  }}>
                    {option.label}
                  </span>
                  {formData.gpaScale === option.value && (
                    <Check size={18} color="#F9550D" aria-label="Selected" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AcademicInfoEditModal;
